import { userService } from './user.service';
import { UserProfile } from '../models';
import { Timestamp } from 'firebase/firestore';

/**
 * Check if a specific day is completed
 */
export async function isDayCompleted(
  userId: string,
  dayNumber: number,
  year: string = '2026'
): Promise<boolean> {
  try {
    const userData = await userService.getUser(userId);
    return (
      userData?.completedDays?.[year]?.some(
        (completion) => completion.dayNumber === dayNumber
      ) || false
    );
  } catch (error) {
    console.error('Error checking if day is completed:', error);
    return false;
  }
}

/**
 * Toggle completion status for a specific day
 */
export async function toggleDayCompletion(
  userId: string,
  dayNumber: number,
  year: string = '2026'
): Promise<void> {
  const isCompleted = await isDayCompleted(userId, dayNumber, year);
  if (isCompleted) {
    await removeCompletedDay(userId, dayNumber, year);
  } else {
    await addCompletedDay(userId, dayNumber, year);
  }
}

/**
 * Add a completed day for a specific year
 */
export async function addCompletedDay(
  userId: string,
  dayNumber: number,
  year: string = '2026'
): Promise<void> {
  try {
    const userData = await userService.getUser(userId);
    const currentCompletedDays = userData?.completedDays || {};

    // Initialize year array if it doesn't exist
    if (!currentCompletedDays[year]) {
      currentCompletedDays[year] = [];
    }

    // Check if day is not already completed
    const alreadyCompleted = currentCompletedDays[year].some(
      (completion) => completion.dayNumber === dayNumber
    );

    // Add day if not already present
    if (!alreadyCompleted) {
      currentCompletedDays[year].push({
        date: Timestamp.now(),
        dayNumber: dayNumber,
      });
      currentCompletedDays[year].sort((a, b) => a.dayNumber - b.dayNumber);

      await userService.createOrUpdateUser(userId, {
        completedDays: currentCompletedDays,
      });
    }
  } catch (error) {
    console.error('Error adding completed day:', error);
    throw error;
  }
}

/**
 * Remove a completed day for a specific year
 */
export async function removeCompletedDay(
  userId: string,
  dayNumber: number,
  year: string = '2026'
): Promise<void> {
  try {
    const userData = await userService.getUser(userId);
    const currentCompletedDays = userData?.completedDays || {};

    if (currentCompletedDays[year]) {
      currentCompletedDays[year] = currentCompletedDays[year].filter(
        (completion) => completion.dayNumber !== dayNumber
      );

      await userService.createOrUpdateUser(userId, {
        completedDays: currentCompletedDays,
      });
    }
  } catch (error) {
    console.error('Error removing completed day:', error);
    throw error;
  }
}

/**
 * Get completed days for a specific year
 */
export async function getCompletedDaysForYear(
  userId: string,
  year: string = '2026'
): Promise<number[]> {
  try {
    const userData = await userService.getUser(userId);
    return (
      userData?.completedDays?.[year]?.map(
        (completion) => completion.dayNumber
      ) || []
    );
  } catch (error) {
    console.error('Error getting completed days for year:', error);
    throw error;
  }
}

/**
 * Get completed day completions (with dates) for a specific year
 */
export async function getCompletedDayCompletions(
  userId: string,
  year: string = '2026'
): Promise<{ date: Timestamp; dayNumber: number }[]> {
  try {
    const userData = await userService.getUser(userId);
    return userData?.completedDays?.[year] || [];
  } catch (error) {
    console.error('Error getting completed day completions:', error);
    throw error;
  }
}

/**
 * Update user profile settings
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<UserProfile>
): Promise<void> {
  try {
    await userService.createOrUpdateUser(userId, updates);
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
}
